/**
 * Self-contained legal documents for the products that carry their own set
 * (ScreenDeck, Chroniq) instead of an annex to the group-wide base text.
 *
 * Why these live in TypeScript and not in assets/translations/*.json:
 * Chroniq addresses employees in Romandie and Ticino, so its privacy policy and
 * terms have to exist in fr/it. The portal UI itself ships de/en only, and adding
 * half-filled fr.json/it.json would make every other page fall back key-by-key.
 * Keeping the documents here lets a legal page render fr/it while the surrounding
 * marketing chrome stays in the session language.
 */

/** Languages a product legal document can be written in. */
export type LegalDocLang = 'de' | 'en' | 'fr' | 'it';

export const LEGAL_DOC_LANGS: readonly LegalDocLang[] = ['de', 'en', 'fr', 'it'];

/** One numbered section of a product legal document. */
export interface LegalDocSection {
    /** Marker shown in the index and beside the heading ('01', '§ 4', …). */
    num: string;
    title: string;
    /**
     * Section body as HTML. Authored in this repository and never derived from user
     * input; Angular still sanitises it on [innerHTML] binding.
     */
    body: string;
}

export interface LegalDoc {
    /** Document heading, e.g. 'Datenschutzerklärung ScreenDeck'. */
    title: string;
    /** Sub-line under the heading — validity and revision date. */
    meta: string;
    /** Heading of the section index ('Inhalt', 'Contents', …). */
    tocHeading: string;
    /** Closing line under the last section. */
    footer: string;
    sections: readonly LegalDocSection[];
}

/** A document in every language it has been translated into. */
export type LegalDocSet = Partial<Record<LegalDocLang, LegalDoc>>;

export function isLegalDocLang(value: string | null | undefined): value is LegalDocLang {
    return !!value && (LEGAL_DOC_LANGS as readonly string[]).includes(value);
}

/**
 * Picks the language a product document is rendered in.
 *
 * `requested` comes from `?lang=` — the platforms link here with the language the
 * reader was already using (e.g. Chroniq's French footer links `?lang=fr`). It wins
 * over the portal's own session language so a French reader is not silently served
 * German. Falls back to German, which every document has.
 */
export function resolveDocLang(doc: LegalDocSet, requested: string | null, sessionLang: string): LegalDocLang {
    if (isLegalDocLang(requested) && doc[requested]) return requested;
    if (isLegalDocLang(sessionLang) && doc[sessionLang]) return sessionLang;
    return 'de';
}
